import type { Project } from '@/api/core-api/types.gen';

import {
  isAiModStudy,
  isModeratedStudy,
  isOnlineTaskProject,
  isQuantitativeStudy,
} from '../../-study-type-helpers';

export const ALL_TAB_IDS = [
  'participants',
  'calendar',
  'bookings',
  'results',
  'details',
  'chat',
  'clips',
  'concepts',
  'insights',
  'messages',
  'completes',
  'payments',
  'analytics',
] as const;

export type TabId = (typeof ALL_TAB_IDS)[number];

/**
 * Tabs that fill the scroll viewport and scroll their panes internally. Other
 * tabs keep their natural height so taller content page-scrolls.
 */
export const VIEWPORT_FILLING_TABS: ReadonlySet<TabId> = new Set<TabId>([
  'messages',
  'participants',
  'chat',
  'insights',
]);

const AI_MOD_TAB_IDS: readonly TabId[] = [
  'participants',
  'results',
  'details',
  'clips',
  'insights',
  'messages',
  'payments',
];

// Survey-style non-AI studies (survey, unmoderated, diary) don't schedule
// against a calendar, so they get neither the Calendar nor the Bookings tab.
const SURVEY_TAB_IDS: readonly TabId[] = [
  'participants',
  'messages',
  'payments',
];

// Moderated studies (1:1 / focus group / in-person) schedule against the
// researcher's calendar, so they additionally get the Calendar and Bookings
// tabs.
const MODERATED_TAB_IDS: readonly TabId[] = [
  'participants',
  'calendar',
  'bookings',
  'messages',
  'payments',
];

/**
 * Whether the study has a concept section: a section asking its questions
 * about at least one concept. A single concept gets the tab too — its write-up
 * and counts, without the comparison framing — because a researcher who tested
 * one thing still wants to see how it landed.
 *
 * Same rule as `listConceptSections` in `common/feature-extraction`, which
 * decides what the engine writes a report for. Restated rather than imported —
 * that package is not aliased into the browser bundle — so the two must move
 * together.
 */
export function hasConceptSections(
  project: Pick<Project, 'interviewSections'>,
): boolean {
  return (project.interviewSections ?? []).some(
    (section) => (section.conceptBlock?.concepts ?? []).length > 0,
  );
}

export function getAllowedTabs(
  project: Pick<Project, 'kindOfResearch' | 'task' | 'interviewSections'>,
  {
    hasQuota = false,
    hasInsightsChat = false,
  }: { hasQuota?: boolean; hasInsightsChat?: boolean } = {},
): readonly TabId[] {
  if (isAiModStudy(project.kindOfResearch)) {
    const withConcepts = hasConceptSections(project);
    return AI_MOD_TAB_IDS.flatMap((id): TabId[] => {
      // Chat sits just before Clips, behind the PostHog `insights_chat` flag.
      if (id === 'clips') return hasInsightsChat ? ['chat', id] : [id];
      // Between Details and Insights, matching the data flow: the per-question
      // detail, then the concept view drawn from it, then the study-level report.
      if (id === 'insights') return withConcepts ? ['concepts', id] : [id];
      return [id];
    });
  }
  if (isModeratedStudy(project.kindOfResearch)) return MODERATED_TAB_IDS;

  // Survey-style tabs, extended by capability:
  // - online-task (integration) projects get a Completes tab after Messages;
  // - the Analytics (quota progress) tab shows only when a quota exists, like
  //   legacy. Only quantitative studies can have quotas. Both can apply at once.
  const tabs: TabId[] = SURVEY_TAB_IDS.flatMap((id): TabId[] =>
    id === 'messages' && isOnlineTaskProject(project)
      ? [id, 'completes']
      : [id],
  );
  if (hasQuota && isQuantitativeStudy(project.kindOfResearch)) {
    tabs.push('analytics');
  }
  return tabs;
}
